import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getActiveRouter } from '@/router'
import { isClient, safeLocalStorage } from '@/composables/useSSR'
import type { MailboxDelegationInfo } from '@/types/dashboard/mail'

const MAIL_JWT_KEY = 'mailJwt'
const MAIL_DELEGATIONS_KEY = 'mailDelegations'

interface MailboxInfo {
  id: string
  email: string
  quota_bytes?: number
  used_bytes?: number
}

interface MailJwtContent {
  sub: string
  email: string
  exp: number
  iat: number
}

function parseMailJwt(token: string): MailJwtContent | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payloadPart = parts[1]
    if (!payloadPart) return null
    const payload = JSON.parse(atob(payloadPart))
    return payload as MailJwtContent
  } catch {
    return null
  }
}

export const useMailAuthStore = defineStore('mailAuth', () => {
  const storage = isClient ? safeLocalStorage() : null
  const initialToken = storage?.getItem(MAIL_JWT_KEY) ?? null

  const token = ref<string | null>(initialToken)
  const mailbox = ref<MailboxInfo | null>(null)
  const delegations = ref<MailboxDelegationInfo[]>([])

  // Restore delegations from localStorage
  if (isClient && storage) {
    try {
      const stored = storage.getItem(MAIL_DELEGATIONS_KEY)
      if (stored) {
        delegations.value = JSON.parse(stored)
      }
    } catch {
      // ignore parse errors
    }
  }

  const getToken = computed(() => token.value)

  const getMailbox = computed(() => mailbox.value)

  const isAuthenticated = computed(() => {
    if (!token.value) return false
    const parsed = parseMailJwt(token.value)
    if (!parsed) return false
    const now = Math.floor(Date.now() / 1000)
    return now < parsed.exp
  })

  const hasDelegations = computed(() => delegations.value.length > 0)

  function hasPermission(domainId: number, perm: string): boolean {
    const delegation = delegations.value.find((d) => d.domain_id === domainId)
    if (!delegation) return false
    if (delegation.expires_at) {
      const expiresAt = new Date(delegation.expires_at)
      if (expiresAt < new Date()) return false
    }
    return delegation.permissions.includes(perm)
  }

  function setAuth(
    newToken: string,
    mailboxInfo: MailboxInfo,
    newDelegations?: MailboxDelegationInfo[]
  ) {
    token.value = newToken
    mailbox.value = mailboxInfo
    safeLocalStorage()?.setItem(MAIL_JWT_KEY, newToken)
    setDelegations(newDelegations ?? [])
  }

  function setDelegations(newDelegations: MailboxDelegationInfo[]) {
    delegations.value = newDelegations
    const storage = safeLocalStorage()
    if (storage) {
      if (newDelegations.length > 0) {
        storage.setItem(MAIL_DELEGATIONS_KEY, JSON.stringify(newDelegations))
      } else {
        storage.removeItem(MAIL_DELEGATIONS_KEY)
      }
    }
  }

  function clearAuth() {
    token.value = null
    mailbox.value = null
    delegations.value = []
    const storage = safeLocalStorage()
    if (storage) {
      storage.removeItem(MAIL_JWT_KEY)
      storage.removeItem(MAIL_DELEGATIONS_KEY)
    }
  }

  function logout() {
    clearAuth()
    const router = getActiveRouter()
    if (!router) return

    const currentRoute = router.currentRoute.value
    if (currentRoute.path.startsWith('/webmail')) {
      router.push({ name: 'webmail' })
    }
  }

  function isTokenValid(): boolean {
    if (!token.value) return false
    const parsed = parseMailJwt(token.value)
    if (!parsed) return false
    const now = Math.floor(Date.now() / 1000)
    return now < parsed.exp
  }

  return {
    token,
    mailbox,
    delegations,
    getToken,
    getMailbox,
    isAuthenticated,
    hasDelegations,
    hasPermission,
    setAuth,
    setDelegations,
    clearAuth,
    logout,
    isTokenValid
  }
})
